import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import PlacesAutocomplete from 'react-places-autocomplete';
import { useGetAllThingsQuery }               from '../../redux/thunks/things/thingsget';
import { useGetPreSignedUrlQuery }             from '../../redux/thunks/files/filespresignurl';
import { useLazyGetPreSignedUrlDocsQuery }     from '../../redux/thunks/files/filespresignurldocs';
import { useEditBusinessMutation }             from '../../redux/thunks/sp/editbusiness';
import { useDeleteBusinessDocumentMutation }   from '../../redux/thunks/files/businessdocdelete';
import { useGetUserProfileQuery }              from '../../redux/thunks/users/userprofile';
import { useSendOtpMutation }                  from '../../redux/thunks/spusers/sendotp';
import { useVerifyOtpMutation }                from '../../redux/thunks/spusers/verifyotp';
import { useSmsOptOutMutation }                from '../../redux/thunks/spusers/smsoptout';

import { spApi }                               from '../../redux/thunks/sp/sp';

import {
  Container, Grid, Paper, Typography, Box, TextField, Button,
  MenuItem, CircularProgress, Skeleton, Chip, Autocomplete,
  IconButton, Stack, Avatar, Divider, Tooltip, Dialog,
  DialogActions, DialogContent, DialogContentText, DialogTitle,
  Alert, LinearProgress, InputAdornment, Collapse, Checkbox,
  FormControlLabel,
  Link,
} from '@mui/material';

import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  History as HistoryIcon,
  Preview as PreviewIcon,
  AutoAwesome as AutoAwesomeIcon,
  Lock as LockIcon,
  UploadFile as UploadFileIcon,
  Info as InfoIcon,
  DeleteOutline as DeleteIcon,
  Upgrade as UpgradeIcon,
  CheckCircle,
  Description as DocumentIcon,
  Sms as SmsIcon,
  Phone as PhoneIcon,
  VerifiedUser as VerifiedIcon,
} from '@mui/icons-material';

// ─── Constants — unchanged ─────────────────────────────────────────────────────

const DOCUMENT_QUOTAS = {
  free:    { maxDocuments: 1,  maxFileSize: 5  * 1024 * 1024, maxTotalStorage: 5   * 1024 * 1024 },
  premium: { maxDocuments: 20, maxFileSize: 50 * 1024 * 1024, maxTotalStorage: 500 * 1024 * 1024 },
};

const formatFileSize = (value, inputUnit = 'bytes') => {
  if (value === 0) return '0 Bytes';
  let bytes = value;
  if (inputUnit === 'mb' || inputUnit === 'MB') bytes = value * 1024 * 1024;
  else if (inputUnit === 'kb' || inputUnit === 'KB') bytes = value * 1024;
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.max(0, Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const BUSINESS_HOURS_OPTIONS = [
  { value: '24/7',     label: 'Open 24/7'                      },
  { value: 'mon_fri',  label: 'Monday-Friday (9 AM - 5 PM)'    },
  { value: 'mon_sat',  label: 'Monday-Saturday (9 AM - 5 PM)'  },
  { value: 'Custom',   label: 'Custom'                          },
];

const TOP_CATEGORIES = [
  // 'Restaurant' (singular) matched neither skillName on the document, nor
  // validateFormData's allowedCategories, nor SEGMENT_CATEGORIES in
  // descriptor-constants.js — all three use the plural. MUI rendered the
  // select BLANK for every restaurant, and selecting the option submitted a
  // value the backend rejects with "Invalid or missing category."
  //
  // Editing a restaurant has therefore been impossible, silently, because
  // handleSave never checks postEdit's response — it navigates back on
  // success and on 400 alike, so the owner believes they saved.
  { id: 1, name: 'Restaurants'                },
  { id: 2, name: 'Retail'                     },
  { id: 3, name: 'Healthcare'                 },
  { id: 4, name: 'Technology'                 },
  { id: 5, name: 'Automotive'                 },
  { id: 6, name: 'Legal'                      },
  { id: 8, name: 'Startup'                    },
  { id: 7, name: 'Other Professional Services'},
];

const STEPS = ['Basic Information', 'Location & Hours', 'Categories & Services', 'Notifications'];

// ─── DocumentQuotaDisplay — unchanged ─────────────────────────────────────────

const DocumentQuotaDisplay = ({ userAccountType, currentDocuments, currentStorage, quotaLimits, isLoading }) => {
  if (isLoading) return <Typography variant="caption" color="textSecondary">Loading document usage...</Typography>;

  const docPercent     = (currentDocuments / quotaLimits.maxDocuments) * 100;
  const storageBytes   = currentStorage * 1024 * 1024;
  const storePercent   = (storageBytes / quotaLimits.maxTotalStorage) * 100;
  const canUpload      = currentDocuments < quotaLimits.maxDocuments;
  const hasStorage     = storageBytes < quotaLimits.maxTotalStorage;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DocumentIcon color="primary" fontSize="small" />
        Document Usage
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="caption" color="textSecondary">Documents</Typography>
          <Typography variant="caption" color="textSecondary">{currentDocuments}/{quotaLimits.maxDocuments}</Typography>
        </Box>
        <LinearProgress variant="determinate" value={Math.min(docPercent, 100)} color={canUpload ? 'primary' : 'error'} sx={{ height: 6, borderRadius: 1, mb: 0.5 }} />
        <Typography variant="caption" color={canUpload ? 'success.main' : 'error.main'}>
          {canUpload ? `${quotaLimits.maxDocuments - currentDocuments} remaining` : 'Limit reached'}
        </Typography>
      </Box>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="caption" color="textSecondary">Storage</Typography>
          <Typography variant="caption" color="textSecondary">{formatFileSize(currentStorage, 'MB')}/{formatFileSize(quotaLimits.maxTotalStorage)}</Typography>
        </Box>
        <LinearProgress variant="determinate" value={Math.min(storePercent, 100)} color={hasStorage ? 'primary' : 'error'} sx={{ height: 6, borderRadius: 1, mb: 0.5 }} />
        <Typography variant="caption" color={hasStorage ? 'success.main' : 'error.main'}>
          {hasStorage ? `${formatFileSize(quotaLimits.maxTotalStorage - storageBytes)} remaining` : 'Storage full'}
        </Typography>
      </Box>
    </Paper>
  );
};

// ─── SMS Opt-in with phone verification ────────────────────────────────────────


const SmsOptInSection = ({ userId, token, userData, isPremium }) => {
  const [phone,       setPhone]       = useState('');
  const [code,        setCode]        = useState('');
  const [step,        setStep]        = useState('idle');   // idle | sending | verify | verifying | done | optout
  const [error,       setError]       = useState('');
  const [countdown,   setCountdown]   = useState(0);
  const [consented, setConsented] = useState(false);
  const [sendOtp]   = useSendOtpMutation();
  const [verifyOtp] = useVerifyOtpMutation();
  const [smsOptOut] = useSmsOptOutMutation();   

  // Initialise from existing user data
  useEffect(() => {
    if (userData?.phoneNumber1) {
      const raw = userData.phoneNumber1.replace(/^\+1/, '');
      const d   = raw.replace(/\D/g, '');
      setPhone(d.length === 10
        ? `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`
        : userData.phoneNumber1
      );
      if (userData.smsOptIn && userData.phoneVerified) setStep('done');
    }
  }, [userData]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const toE164 = (formatted) => {
    const d = formatted.replace(/\D/g, '');
    if (d.length === 10) return `+1${d}`;
    if (d.length === 11 && d.startsWith('1')) return `+${d}`;
    return null;
  };

  const formatPhone = (value) => {
    const d = value.replace(/\D/g, '');
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0,3)}) ${d.slice(3)}`;
    return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6,10)}`;
  };

  const handleSendOtp = async () => {
    setError('');
    const e164 = toE164(phone);
    if (!e164) { setError('Please enter a valid 10-digit US number.'); return; }

    setStep('sending');
    try {
      await sendOtp({ payload: { phoneNumber: e164 } }).unwrap();
      setStep('verify');
      setCountdown(60);
    } catch (e) {
      setError(e.data?.message || 'Failed to send code');
      setStep('idle');
    }
  };

  const handleVerifyOtp = async () => {
    setError('');
    const e164 = toE164(phone);
    if (!code || code.length < 6) { setError('Enter the 6-digit code'); return; }

    setStep('verifying');
    try {
      const result = await verifyOtp({ payload: { phoneNumber: e164, code } }).unwrap();
      setStep(result.smsEnabled ? 'done' : 'verified_free');
    } catch (e) {
      setError(e.data?.message || 'Verification failed');
      setStep('verify');
    }
  };

  const handleOptOut = async () => {
    try {
      await smsOptOut().unwrap();
      setStep('idle');
      setCode('');
    } catch (e) {
      setError('Failed to opt out. Please try again.');
    }
  };

  if (!isPremium) {
    return (
      <Alert
        severity="info"
        sx={{ borderRadius: 2 }}
        action={
          <Button color="inherit" size="small" href="/premium">Upgrade</Button>
        }
      >
        SMS draft notifications are available on Premium. Upgrade to get a text
        the moment a new draft is ready for approval.
      </Alert>
    );
  }

  if (step === 'done') {
    return (
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <VerifiedIcon color="success" />
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color="success.main">
                SMS notifications enabled
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {phone} — verified ✓
              </Typography>
            </Box>
          </Box>
          <Button
            size="small"
            color="error"
            variant="outlined"
            onClick={handleOptOut}
            sx={{ borderRadius: 1.5, textTransform: 'none' }}
          >
            Turn off
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          You'll receive a text when new drafts are ready. Reply STOP to opt out at any time.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'primary.50', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <SmsIcon color="primary" fontSize="small" />
        </Box>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>SMS Draft Notifications</Typography>
            <Chip label="Premium" size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem' }} />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Get a text the moment a new draft is ready to approve
          </Typography>
        </Box>
      </Box>

      {/* What you get */}
      <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1.5 }}>
        {[
          'Text when a review response draft is ready',
          'Text when a social media post is ready',
          'One-tap approve or reject links in every message',
          'Typically 1 to 3 messages per week',
        ].map(item => (
          <Box key={item} sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
            <CheckCircle sx={{ fontSize: 14, color: 'success.main', mt: 0.2, flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary">{item}</Typography>
          </Box>
        ))}
      </Box>

      {/* Step 1 — phone input */}
      <TextField
        fullWidth
        size="small"
        label="Mobile Phone Number"
        placeholder="(555) 555-5555"
        value={phone}
        onChange={(e) => { setPhone(formatPhone(e.target.value)); setError(''); }}
        disabled={step === 'verify' || step === 'verifying' || step === 'sending'}
        inputProps={{ maxLength: 14, type: 'tel', inputMode: 'numeric' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PhoneIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ ml: 0.5, color: 'text.secondary' }}>+1</Typography>
            </InputAdornment>
          )
        }}
        helperText="US numbers only. Standard message and data rates may apply."
        sx={{ mb: 1.5 }}
      />

      {/* Step 2 — OTP input */}
      <Collapse in={step === 'verify' || step === 'verifying'}>
        <TextField
          fullWidth
          size="small"
          label="6-digit verification code"
          placeholder="123456"
          value={code}
          onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
          inputProps={{ inputMode: 'numeric', maxLength: 6 }}
          helperText={`Code sent to ${phone}. ${countdown > 0 ? `Resend in ${countdown}s` : ''}`}
          sx={{ mb: 1.5 }}
        />
      </Collapse>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: 1.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Consent checkbox — required before send =====  */}
      {(step === 'idle' || step === 'sending') && (
        <>
          <FormControlLabel
            sx={{ alignItems: 'flex-start', mb: 1.5 }}
            control={
              <Checkbox
                checked={consented}
                onChange={(e) => setConsented(e.target.checked)}
                size="small"
                sx={{ mt: -0.5, flexShrink: 0 }}
                // NOT pre-checked — Twilio requirement
              />
            }
            label={
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                I agree to receive automated SMS notifications from Revfer
                about draft content ready for my approval. Message frequency
                varies based on business activity — typically 1 to 3 messages
                per week. Message and data rates may apply. Reply{' '}
                <Box component="strong">HELP</Box> for help or{' '}
                <Box component="strong">STOP</Box> to cancel at any time.
                By checking this box I confirm I am the mobile account holder
                or have the account holder's permission. View our{' '}
                <Link href="/terms" target="_blank" underline="hover"
                  sx={{ color: 'primary.main', fontWeight: 600 }}>
                  Terms of Service
                </Link>
                {' '}and{' '}
                <Link href="/privacy" target="_blank" underline="hover"
                  sx={{ color: 'primary.main', fontWeight: 600 }}>
                  Privacy Policy
                </Link>.
              </Typography>
            }
          />

          <Button
            fullWidth
            variant="contained"
            size="medium"
            onClick={handleSendOtp}
            disabled={
              step === 'sending'                    ||
              phone.replace(/\D/g, '').length < 10  ||
              !consented                             // ← gated on checkbox
            }
            startIcon={
              step === 'sending'
                ? <CircularProgress size={16} color="inherit" />
                : <SmsIcon />
            }
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {step === 'sending' ? 'Sending code...' : 'Send verification code'}
          </Button>
        </>
      )}

 

      {(step === 'verify' || step === 'verifying') && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            onClick={handleVerifyOtp}
            disabled={step === 'verifying' || code.length < 6}
            startIcon={step === 'verifying' ? <CircularProgress size={16} color="inherit" /> : <VerifiedIcon />}
            sx={{ flex: 2, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {step === 'verifying' ? 'Verifying...' : 'Verify code'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleSendOtp}
            disabled={countdown > 0 || step === 'verifying'}
            sx={{ flex: 1, borderRadius: 2, textTransform: 'none' }}
          >
            {countdown > 0 ? `${countdown}s` : 'Resend'}
          </Button>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}>
        Message and data rates may apply · Reply STOP to cancel · Carriers not liable for delays
      </Typography>
    </Paper>
  );
};

// ─── EditProgress — updated for 4 steps ───────────────────────────────────────

const EditProgress = ({ currentStep, isLoading }) => {
  if (isLoading) {
    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Skeleton variant="text" width="60%" height={32} />
        {STEPS.map((_, i) => <Skeleton key={i} variant="rectangular" height={50} sx={{ my: 1 }} />)}
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, border: 'none' }}>
      <Typography variant="h6" gutterBottom>Edit Progress</Typography>
      <Box sx={{ mt: 2 }}>
        {STEPS.map((step, index) => {
          const isCompleted = index < currentStep;
          const isCurrent   = index === currentStep;
          return (
            <Box key={index} sx={{
              p: 1.5, mb: 1, borderRadius: 1,
              bgcolor:     isCurrent ? 'primary.50'  : isCompleted ? 'success.50'  : 'grey.50',
              border:      '1px solid',
              borderColor: isCurrent ? 'primary.main' : isCompleted ? 'success.main' : 'transparent',
              color:       isCurrent ? 'primary.main' : isCompleted ? 'success.main' : 'text.secondary',
            }}>
              <Typography variant="body2" fontWeight={isCurrent ? 600 : 400}>
                {index + 1}. {step}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <Box sx={{ mt: 1.5 }}>
        <LinearProgress
          variant="determinate"
          value={((currentStep + 1) / STEPS.length) * 100}
          sx={{ height: 4, borderRadius: 2 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}>
          Step {currentStep + 1} of {STEPS.length}
        </Typography>
      </Box>
    </Paper>
  );
};

// ─── BusinessPreview — unchanged ──────────────────────────────────────────────

const BusinessPreview = ({ formData, isLoading }) => {
  if (isLoading) {
    return (
      <Paper sx={{ p: 2 }}>
        <Skeleton variant="text" width="60%" height={32} />
        <Skeleton variant="circular" width={80} height={80} sx={{ my: 2 }} />
        <Skeleton variant="text" width="80%" />
      </Paper>
    );
  }
  return (
    <Paper variant="outlined" sx={{ p: 2, border: 'none', mb: 2 }}>
      <Typography variant="h6" gutterBottom>Preview</Typography>
      <Box sx={{ textAlign: 'center', my: 3 }}>
        <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: 'primary.light' }}>
          {formData.businessName?.substring(0, 2).toUpperCase()}
        </Avatar>
        <Typography variant="h6">{formData.businessName}</Typography>
        <Typography variant="body2" color="text.secondary">{formData.skillName}</Typography>
        <Box sx={{ mt: 2 }}>
          {formData.additionalCategories?.map((cat) => (
            <Chip key={cat.id} label={cat.name} size="small" sx={{ m: 0.5 }} />
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────

const EditBusinessPage = () => {
  const navigate  = useNavigate();
  const dispatch  = useDispatch();
  const location  = useLocation();

  const { businessId }   = useParams();
  const businessDetails  = location.state;

  const token  = useSelector((state) => state.auth?.token   || null);
  const userId = useSelector((state) => state.auth?.userId  || null);
  const spId   = useSelector((state) => state.auth?.user?.spId);

  const [currentStep,      setCurrentStep]      = useState(0);
  const [isLoading,        setIsLoading]        = useState(false);
    // formData was `useState(businessDetails)` — location.state used RAW, which
  // is profileData from the public getbusinessbyid. Several payload names do
  // not match what this form reads, so those fields rendered BLANK and saved
  // as "", erasing stored data:
  //
  //   businessDescription → description       (blanked the description)
  //   businessWebsite     → website           (blanked the website)
  //   businessAddress     → address           (blanked it, and made
  //                                            editbusinessprocess re-geocode
  //                                            on every save with an empty
  //                                            string — the call that threw
  //                                            and killed the creation
  //                                            pipeline for nine months)
  //   telephone           → phone             (had an inline fallback)
  //   spEmail             → email             (had an inline fallback)
  //   category/categoryIds→ additionalCategories
  //
  // Normalising once here means every field renders populated AND submits
  // correctly, instead of each read site needing its own fallback — which is
  // how phone and email got fixed and the other four were missed.
  const [formData, setFormData] = useState(() => {
    const b = businessDetails || {};
    return {
      ...b,
      description: b.description ?? b.businessDescription ?? '',
      website:     b.website     ?? b.businessWebsite     ?? '',
      // businessAddress is what the owner typed and what editbusinessprocess
      // compares against to decide whether to re-geocode. `address` is a
      // truncated street-only derivative written by the geocoder — "301 Falls
      // Boulevard" against a stored "301 Falls Blvd, Quincy MA 2169".
      //
      // Preferring `address` showed the owner a partial address, and
      // submitting it back read as a change on every save, re-running the
      // geocoder against a street with no city or state. That call threw on
      // bad input in Phase 2 and killed the creation pipeline for nine months;
      // it has a try/catch now, so it degrades rather than kills — but it
      // still wastes a Google call and can overwrite geocodeResults with a
      // worse match.
      address:     b.businessAddress ?? b.address ?? '',
      phone:       b.phone       ?? b.telephone           ?? '',
      email:       b.email       ?? b.spEmail             ?? '',
      // The payload carries names and ids as parallel arrays; the Autocomplete
      // wants objects. Undefined here meant the backend received no
      // additionalCategories and wrote category:[] and categoryIds:[] — which
      // it does UNCONDITIONALLY, so every save wiped them.
      additionalCategories: b.additionalCategories ?? null,
    };
  });
  const [errors,           setErrors]           = useState({});
  const [uploadErrors,     setUploadErrors]     = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  const argsPreSignedUrl = { eventType: 'BusinessProfilePhotos', businessId };
  const { data: presignedUrlData }                                             = useGetPreSignedUrlQuery(argsPreSignedUrl);
  const [getPreSignedDocs, { isLoading: preSignedUrlDocsLoading }]             = useLazyGetPreSignedUrlDocsQuery();
  const [postEdit,         { isLoading: isEditBusinessLoading }]               = useEditBusinessMutation();
  const [deleteBusinessDocument, { isLoading: isDeleting }]                   = useDeleteBusinessDocumentMutation();
  const { data: userData, isLoading: isUserLoading }                           = useGetUserProfileQuery(userId);
  const { data: categoriesData,  isLoading: iscategoriesDataLoading }          = useGetAllThingsQuery({ spId, authType: 'token', token });

  const userAccountType = userData?.accountDetails?.type || 'free';
  const quotaLimits     = DOCUMENT_QUOTAS[userAccountType] || DOCUMENT_QUOTAS.free;
  const isPremium       = userAccountType === 'premium';

  const isBusinessOwner = useMemo(() => {
    if (!businessDetails || !userId) return false;
    return businessDetails.spUserId === userId;
  }, [businessDetails, userId]);

  const { currentDocuments, currentStorage } = useMemo(() => ({
    currentDocuments: userData?.quotaUsage?.document_uploads?.lifetime   || 0,
    currentStorage:   userData?.quotaUsage?.document_storage?.lifetime   || 0,
  }), [userData]);

  const canUploadDocuments = isBusinessOwner &&
    (userAccountType === 'premium' || currentDocuments < quotaLimits.maxDocuments);
  const hasStorageSpace    = (currentStorage * 1024 * 1024) < quotaLimits.maxTotalStorage;

  // Init existing docs
  useEffect(() => {
    if (businessDetails?.businessDocsUrl && !formData.existingDocs) {
      setFormData(prev => ({
        ...prev,
        existingDocs: businessDetails.businessDocsUrl.map(url => ({
          url,
          name: url.split('/').pop()
        }))
      }));
    }
  }, [businessDetails]);

    // categoryIds is empty on seeded businesses, so the stored category NAMES
  // have to be matched back against the live skills list to recover their ids.
  // getallskills returns all 762 with shape { name, skillId, ... }.
  //
  // Names that do not resolve are dropped — Zella's Pizzeria carries 'Pizza'
  // and 'Caterers' from directory import, and neither exists in `skills`. The
  // backend's categoriesResolved guard is what stops those being erased; this
  // effect only decides what the owner sees in the Autocomplete.
  useEffect(() => {
    if (formData.additionalCategories || !categoriesData?.data) return;
    const names = businessDetails?.category || [];
    if (!names.length) {
      setFormData(prev => ({ ...prev, additionalCategories: [] }));
      return;
    }
    const byName = new Map(categoriesData.data.map(c => [c.name, c]));
    const resolved = names.map(n => byName.get(n)).filter(Boolean);
    if (resolved.length !== names.length) {
      console.warn('[edit] unresolvable category names:',
        names.filter(n => !byName.has(n)));
    }
    setFormData(prev => ({ ...prev, additionalCategories: resolved }));
  }, [categoriesData, businessDetails]);

  // ===== Handlers =====

  const uploadPhotoToS3 = async (file, presignedUrl) => {
    const res = await fetch(presignedUrl, {
      method:  'PUT',
      body:    file,
      headers: { 'Content-Type': file.type, 'x-amz-meta-eventtype': 'BusinessProfilePhotos' }
    });
    if (!res.ok) throw new Error('Failed to upload photo');
    return true;
  };

  const uploadDocumentToS3 = async (file, presignedUrl) => {
    const res = await fetch(presignedUrl, {
      method:  'PUT',
      body:    file,
      headers: {
        'Content-Type':                file.type,
        'x-amz-meta-eventtype':        'BusinessDocuments',
        'x-amz-meta-documenttype':     file.name.split('.').pop().toLowerCase()
      }
    });
    if (!res.ok) throw new Error('Failed to upload document');
    return true;
  };

  const validateDocuments = (files) => {
    const validationErrors = [];
    const validFiles       = [];
    if (!isBusinessOwner) {
      validationErrors.push('Only business owners can upload documents.');
      return { validFiles: [], validationErrors };
    }
    if (currentDocuments + files.length > quotaLimits.maxDocuments) {
      validationErrors.push(`Document limit: ${quotaLimits.maxDocuments - currentDocuments} remaining.`);
    }
    let totalNewSize = 0;
    files.forEach(file => {
      if (!/\.(pdf|doc|docx|json|txt)$/i.test(file.name)) {
        validationErrors.push(`"${file.name}" — unsupported format.`); return;
      }
      if (file.size > quotaLimits.maxFileSize) {
        validationErrors.push(`"${file.name}" exceeds ${formatFileSize(quotaLimits.maxFileSize)}.`); return;
      }
      totalNewSize += file.size;
      validFiles.push(file);
    });
    const currentBytes  = currentStorage * 1024 * 1024;
    if (currentBytes + totalNewSize > quotaLimits.maxTotalStorage) {
      validationErrors.push(`Storage limit: ${formatFileSize(quotaLimits.maxTotalStorage - currentBytes)} remaining.`);
    }
    return { validFiles, validationErrors };
  };

  const handleDocumentChange = (e) => {
    const files = Array.from(e.target.files);
    setUploadErrors([]);
    const { validFiles, validationErrors } = validateDocuments(files);
    if (validationErrors.length > 0) { setUploadErrors(validationErrors); return; }
    setFormData(prev => ({
      ...prev,
      pendingDocuments: [
        ...(prev.pendingDocuments || []),
        ...validFiles.map(file => ({ file, name: file.name, type: file.type, size: file.size }))
      ]
    }));
  };

  const handleDocumentDelete = (type, index) => {
    if (type === 'pending') {
      setFormData(prev => ({
        ...prev,
        pendingDocuments: prev.pendingDocuments.filter((_, i) => i !== index)
      }));
    } else {
      setDocumentToDelete({ type, index });
      setDeleteDialogOpen(true);
    }
  };

  const confirmDeleteDocument = async () => {
    try {
      if (documentToDelete?.type === 'existing') {
        const doc = formData.existingDocs[documentToDelete.index];
        await deleteBusinessDocument({ businessId, documentKey: doc.name }).unwrap();
        setFormData(prev => ({
          ...prev,
          existingDocs: prev.existingDocs.filter((_, i) => i !== documentToDelete.index)
        }));
      }
    } catch (e) {
      console.error('Error deleting document:', e);
    } finally {
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (_, value) => {
    handleChange({ target: { name: 'additionalCategories', value } });
  };

  // ← FIXED: handleCancel now navigates back
  const handleCancel = () => {
    navigate(`/business/${businessId}/${formData.businessName?.replace(/[\s,\.]+/g, '-').toLowerCase()}`);
  };

  const handleNext = () => setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
  const handleBack = () => setCurrentStep(prev => Math.max(prev - 1, 0));

  const handleSave = async () => {
    try {
      setIsLoading(true);
      setErrors(e => ({ ...e, submit: null }));

      let photoUrl = formData.photoUrl;
      if (formData.photo && presignedUrlData?.url) {
        await uploadPhotoToS3(formData.photo, presignedUrlData.url);
        photoUrl = presignedUrlData.url.split('?')[0];
      }

      const uploadedDocuments = [];
      for (const docInfo of (formData.pendingDocuments || [])) {
        try {
          const presignedRes = await getPreSignedDocs({
            eventType:    'BusinessDocuments',
            businessId,
            documentType: docInfo.file.name.split('.').pop().toLowerCase()
          }).unwrap();
          if (presignedRes?.url) {
            await uploadDocumentToS3(docInfo.file, presignedRes.url);
            uploadedDocuments.push({ name: docInfo.name, url: presignedRes.url.split('?')[0], keyPath: presignedRes.keyPath });
          }
        } catch (e) {
          console.error(`Error uploading ${docInfo.name}:`, e);
        }
      }

        const updatedData = {
          businessName:         formData.businessName,
          skillName:            formData.skillName,
          category:             formData.skillName,
          // formData is normalised at init, so these are populated and correct.
          // ?? not || so an owner who deliberately clears a field still clears it.
          phone:                formData.phone            ?? '',
          email:                formData.email            ?? '',
          website:              formData.website          ?? '',
          description:          formData.description      ?? '',
          address:              formData.address          ?? '',
          businessHours:        formData.businessHours    ?? '',
          additionalInfo:       formData.additionalInfo   ?? '',
          additionalCategories: formData.additionalCategories,
          specialFeatures:      formData.specialFeatures  ?? '',
          keywords:             formData.keywords         ?? '',
          googlePlaceId:        formData.googlePlaceId    ?? '',
      };

      if (photoUrl !== formData.originalPhotoUrl) {
        updatedData.photoUrl = photoUrl;
      }
      if (uploadedDocuments.length > 0) {
        updatedData.documents = [...(formData.documents || []), ...uploadedDocuments];
      }

      try {
        await postEdit({
          payload: { auth: { token, authType: 'token' }, spId, data: updatedData, businessId }
        }).unwrap();
      } catch (err) {
        // Without .unwrap() a rejected mutation resolves and this navigates
        // back regardless, so a 400 looks exactly like a save. That is how a
        // one-character category mismatch went unnoticed: every restaurant
        // owner who edited their business was told nothing and returned to a
        // page showing their old data.
        const detail = err?.data?.errors?.join(' ') || err?.data?.message || 'Please try again.';
        setErrors({ submit: `Could not save: ${detail}` });
        setIsLoading(false);
        return;
      }

      dispatch(spApi.util.invalidateTags([{ type: 'businessDetails', id: businessId }]));
      navigate(`/business/${businessId}/${formData.businessName.replace(/[\s,\.]+/g, '-').toLowerCase()}`);

    } catch (e) {
      console.error('Error saving changes:', e);
    } finally {
      setIsLoading(false);
    }
  };

  // ===== Render form steps =====

  const renderEditForm = () => {
    switch (currentStep) {

      case 0: return (
        <>
          <Typography variant="h6" gutterBottom>Basic Information</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField fullWidth label="Business Name" name="businessName"
                value={formData.businessName} onChange={handleChange} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth select label="Primary Category" name="skillName"
                value={formData.skillName} onChange={handleChange} required>
                {TOP_CATEGORIES.map(c => <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
                <TextField fullWidth label="Phone Number" name="phone"
                value={formData.phone || ''} onChange={handleChange} required />
            </Grid>
            <Grid item xs={12}>
                <TextField fullWidth label="Email" name="email" type="email"
                value={formData.email || ''} onChange={handleChange} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Website" name="website"
                value={formData.website || ''} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={4} label="Business Description"
                name="description" value={formData.description || ''} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>Business Photo</Typography>
              {formData.photoUrl && (
                <Box sx={{ mb: 2 }}>
                  <img src={formData.photoUrl} alt="Current business"
                    style={{ maxWidth: 200, maxHeight: 200, objectFit: 'cover', borderRadius: 4 }} />
                </Box>
              )}
              <Button variant="outlined" component="label" fullWidth sx={{ height: 80 }}>
                {formData.photo ? 'Change Photo' : formData.photoUrl ? 'Update Photo' : 'Upload Photo'}
                <input type="file" hidden accept="image/*"
                  onChange={(e) => { const f = e.target.files[0]; if (f) setFormData(p => ({ ...p, photo: f })); }} />
              </Button>
              {formData.photo && (
                <Typography variant="body2" sx={{ mt: 1 }}>Selected: {formData.photo.name}</Typography>
              )}
            </Grid>
          </Grid>
        </>
      );

      case 1: return (
        <>
          <Typography variant="h6" gutterBottom>Location & Hours</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>Business Address</Typography>
              <PlacesAutocomplete
                value={formData.address}
                onChange={(address) => handleChange({ target: { name: 'address', value: address } })}
              >
                {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                  <Box>
                    <TextField {...getInputProps({ label: 'Business Address', fullWidth: true, required: true })} />
                    {suggestions.length > 0 && (
                      <Paper sx={{ mt: 1 }}>
                        {loading && <CircularProgress size={20} sx={{ m: 1 }} />}
                        {suggestions.map(s => (
                          <MenuItem {...getSuggestionItemProps(s)} key={s.placeId}>{s.description}</MenuItem>
                        ))}
                      </Paper>
                    )}
                  </Box>
                )}
              </PlacesAutocomplete>
            </Grid>

            {/* ← googlePlaceId field added */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="googlePlaceId"
                label="Google Place ID (Optional)"
                value={formData.googlePlaceId || ''}
                onChange={handleChange}
                placeholder="e.g. ChIJN1t_tDeuEmsRUsoyG83frY4"
                helperText="Used to pull your Google reviews automatically."
              />
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" component="a"
                  href="https://developers.google.com/maps/documentation/javascript/examples/places-placeid-finder"
                  target="_blank" rel="noopener noreferrer"
                  sx={{ color: 'primary.main', textDecoration: 'none' }}>
                  How to find your Place ID ↗
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField select fullWidth label="Business Hours" name="businessHours"
                value={formData.businessHours} onChange={handleChange}>
                {BUSINESS_HOURS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>

            {/* Document upload — unchanged */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 3, border: '1px dashed', borderColor: isBusinessOwner ? 'primary.main' : 'grey.400', bgcolor: 'background.default' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <AutoAwesomeIcon color="primary" />
                  <Typography variant="h6">AI Business Analysis</Typography>
                </Box>
                {isBusinessOwner && (
                  <DocumentQuotaDisplay
                    userAccountType={userAccountType}
                    currentDocuments={currentDocuments}
                    currentStorage={currentStorage}
                    quotaLimits={quotaLimits}
                    isLoading={isUserLoading}
                  />
                )}
                {uploadErrors.length > 0 && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {uploadErrors.map((e, i) => <Typography key={i} variant="body2">• {e}</Typography>)}
                  </Alert>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Upload business documents for AI-powered analysis and enhanced customer assistance.
                </Typography>
                {formData.existingDocs?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Existing Documents</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {formData.existingDocs.map((doc, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center' }}>
                          <Chip label={doc.name} onDelete={() => handleDocumentDelete('existing', i)}
                            disabled={isDeleting && documentToDelete?.index === i}
                            color="primary" variant="outlined"
                            deleteIcon={isDeleting && documentToDelete?.index === i ? <CircularProgress size={16} /> : <DeleteIcon />}
                          />
                          <Tooltip title="View Document">
                            <IconButton size="small" onClick={() => window.open(doc.url, '_blank')} sx={{ ml: 0.5 }}>
                              <PreviewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
                {formData.pendingDocuments?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>To Upload</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {formData.pendingDocuments.map((doc, i) => (
                        <Chip key={i} label={`${doc.name} (${formatFileSize(doc.size)})`}
                          onDelete={() => handleDocumentDelete('pending', i)} sx={{ bgcolor: 'action.hover' }} />
                      ))}
                    </Box>
                  </Box>
                )}
                <Button variant="outlined" component="label" fullWidth startIcon={<UploadFileIcon />}
                  disabled={!isBusinessOwner || !canUploadDocuments || !hasStorageSpace || preSignedUrlDocsLoading}
                  sx={{ height: 80, borderStyle: 'dashed' }}>
                  {preSignedUrlDocsLoading ? <CircularProgress size={24} />
                    : !isBusinessOwner ? 'Business Owners Only'
                    : !canUploadDocuments ? 'Document Limit Reached'
                    : !hasStorageSpace ? 'Storage Full'
                    : 'Upload Business Documents'}
                  <input type="file" hidden accept=".pdf,.doc,.docx,.json,.txt"
                    onChange={handleDocumentChange} multiple
                    disabled={!isBusinessOwner || !canUploadDocuments || !hasStorageSpace} />
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Supported: PDF, DOC, DOCX, JSON, TXT — Max {formatFileSize(quotaLimits.maxFileSize)} per file
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth multiline rows={4} name="additionalInfo"
                value={formData.additionalInfo || ''} onChange={handleChange}
                label="Additional Information"
                placeholder="Hours, special conditions, seasonal changes..."
              />
            </Grid>
          </Grid>
        </>
      );

      case 2: return (
        <>
          <Typography variant="h6" gutterBottom>Categories & Services</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={categoriesData?.data || []}
                value={formData.additionalCategories || []}
                getOptionLabel={(o) => o?.name || ''}
                onChange={handleCategoryChange}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" placeholder="Search categories" helperText="Select multiple categories" fullWidth />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((o, i) => {
                    const { key, ...chipProps } = getTagProps({ index: i });
                    return <Chip key={key} {...chipProps} label={o?.name} color="primary" variant="outlined" />;
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={4} label="Special Features"
                name="specialFeatures" value={formData.specialFeatures || ''} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Keywords/Tags" name="keywords"
                value={formData.keywords || ''} onChange={handleChange}
                helperText="Separate with commas" />
            </Grid>
          </Grid>
        </>
      );

      case 3: return (
        <>
          <Typography variant="h6" gutterBottom>Notification Preferences</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Get notified when AI generates new content drafts for your business.
            Approving or rejecting a draft only takes seconds.
          </Typography>

          {/* Email — always on */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CheckCircle color="success" fontSize="small" />
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="success.main">
                  Email notifications enabled
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Drafts sent to {userData?.email} — includes approve and reject links
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* SMS opt-in */}
          <SmsOptInSection
            userId={userId}
            token={token}
            userData={userData}
            isPremium={isPremium}
          />
        </>
      );

      default: return null;
    }
  };

  // ===== Tips by step =====
  const TIPS = [
    ['Keep business name consistent with official documents', 'Choose the most relevant primary category', 'Provide active contact information'],
    ['Enter complete street address', 'Add your Google Place ID to pull Google reviews automatically', 'Upload documents to enhance AI assistance'],
    ['Select all relevant categories', 'Highlight unique features and services', 'Use specific keywords to improve visibility'],
    ['Email notifications are always on — no setup needed', 'SMS notifications let you approve drafts in one tap', 'Phone number is verified before SMS is enabled'],
  ];

  return (
    <>
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 2, mb: 3 }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h5">Edit Business Details</Typography>
            <Button color="inherit" variant="outlined" size="small" onClick={handleCancel}
              sx={{ borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: 'white' } }}>
              Cancel
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ pb: 6 }}>
        <Grid container spacing={3}>

          {/* Left */}
          <Grid item xs={12} md={3}>
            <EditProgress currentStep={currentStep} isLoading={isLoading} />
          </Grid>

          {/* Middle */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 3, border: 'none' }}>
              {renderEditForm()}

              <Divider sx={{ my: 3 }} />
              {/* Save failures were previously invisible: handleSave did not
                  unwrap the mutation, so a 400 navigated back exactly like a
                  success and the owner believed they had saved. A one-word
                  category mismatch went unnoticed this way. */}
              {errors.submit && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrors(e => ({ ...e, submit: null }))}>
                  {errors.submit}
                </Alert>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button onClick={handleBack} disabled={currentStep === 0 || isLoading} variant="outlined">
                  ← Back
                </Button>
                <Typography variant="body2" color="text.secondary">
                  Step {currentStep + 1} of {STEPS.length}
                </Typography>
                {currentStep < STEPS.length - 1 ? (
                  <Button variant="contained" onClick={handleNext} disabled={isLoading}>
                    Next →
                  </Button>
                ) : (
                  <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={isLoading}>
                    {isLoading ? <CircularProgress size={22} /> : 'Save Changes'}
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Right */}
          <Grid item xs={12} md={3}>
            <BusinessPreview formData={formData} isLoading={isLoading} />
            <Paper variant="outlined" sx={{ p: 2, border: 'none' }}>
              <Typography variant="h6" gutterBottom>Tips</Typography>
              <Box sx={{ mt: 1 }}>
                {TIPS[currentStep]?.map((tip, i) => (
                  <Typography key={i} variant="body2" sx={{ mb: 1 }}>• {tip}</Typography>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Delete dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this document? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteDocument} color="error" variant="contained" disabled={isDeleting}>
            {isDeleting ? <CircularProgress size={22} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditBusinessPage;